import { ref } from 'vue'
import type { Router } from 'vue-router'

export interface UpdateTipConfig {
	id: string
	route: string
	selector: string
	position: 'top' | 'bottom' | 'left' | 'right'
	offsetX?: number
	offsetY?: number
	name?: string
	content: string
	url?: string
	poster?: string
	priority?: number // 新增优先级字段
}

interface VisibilityOptions {
	minWidth: number
	minHeight: number
	minOpacity: number
	stableTime: number
}

interface WatcherState {
	config: UpdateTipConfig
	element?: HTMLElement
	isVisible: boolean
	visibleStartTime: number
	checkTimer?: number
	hasShown: boolean
	elementCheckTimer?: number
	elementCheckCount: number
}
import { addBubbleRecord } from '@/api/guide'

class UpdateTipsManager {
	private configs: UpdateTipConfig[] = []
	private watchers = new Map<string, WatcherState>()
	private mutationObserver: MutationObserver | null = null
	private currentRoute = ''
	private shownTips = new Set<string>()
	private pendingTips: UpdateTipConfig[] = [] // 等待显示的下一个提示
	private isShowingTip = false

	public currentTip = ref<UpdateTipConfig | null>(null)
	public isVisible = ref(false)
	public tipPosition = ref<Record<string, any>>({})

	constructor() {
		this.setupMutationObserver()
		try {
			const saved = localStorage.getItem('update-tips-shown')
			if (saved) {
				this.shownTips = new Set(JSON.parse(saved))
			}
		} catch (error) {
			console.warn('Failed to load shown tips:', error)
		}
	}

	setRouter(router: Router) {
		this.currentRoute = router.currentRoute.value.path
		router.afterEach(to => {
			this.currentRoute = to.path
			this.handleRouteChange()
		})
	}

	setConfigs(configs: UpdateTipConfig[]) {
		// 按照priority排序，priority高的先显示
		this.configs = configs.sort((a, b) => (b.priority || 0) - (a.priority || 0))
		this.clearWatchers()
		this.startWatchingCurrentRoute()
	}

	private setupMutationObserver() {
		this.mutationObserver = new MutationObserver(mutations => {
			let shouldCheck = false
			mutations.forEach(mutation => {
				if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
					shouldCheck = true
				}
			})
			if (shouldCheck) {
				setTimeout(() => {
					this.checkForNewElements()
				}, 100)
			}
		})
		this.mutationObserver.observe(document.body, {
			childList: true,
			subtree: true
		})
	}

	private handleRouteChange() {
		this.clearWatchers()
		this.closeTip()
		this.isShowingTip = false
		this.pendingTips = [] // 清空待显示队列

		setTimeout(() => {
			this.startWatchingCurrentRoute()
		}, 200)
	}

	private startWatchingCurrentRoute() {
		// 过滤出当前路由下未显示过的提示
		const routeConfigs = this.configs.filter(
			config => this.currentRoute.includes(config.route) && !this.shownTips.has(config.id)
		)

		// 如果有多个提示，只监听优先级最高的那个
		if (routeConfigs.length > 0) {
			const highestPriorityConfig = routeConfigs[0]
			this.startWatching(highestPriorityConfig)

			// 其余的加入待显示队列
			if (routeConfigs.length > 1) {
				this.pendingTips = routeConfigs.slice(1)
			}
		}
	}

	private startWatching(config: UpdateTipConfig) {
		if (this.watchers.has(config.id) || this.shownTips.has(config.id)) {
			return
		}

		const watcher: WatcherState = {
			config,
			element: undefined,
			isVisible: false,
			visibleStartTime: 0,
			hasShown: false,
			elementCheckCount: 0
		}

		this.watchers.set(config.id, watcher)
		this.checkElementExistence(config.id)
	}

	private checkElementExistence(configId: string) {
		const watcher = this.watchers.get(configId)
		if (!watcher || watcher.hasShown) return

		const config = watcher.config
		const element = document.querySelector(config.selector) as HTMLElement

		if (element) {
			watcher.element = element
			this.startVisibilityCheck(configId)
		} else {
			watcher.elementCheckCount++
			const maxChecks = 100
			if (watcher.elementCheckCount < maxChecks) {
				watcher.elementCheckTimer = window.setTimeout(() => {
					this.checkElementExistence(configId)
				}, 100)
			} else {
				console.warn(`Element not found after ${maxChecks} attempts: ${config.selector}`)
				this.watchers.delete(configId)
				// 如果元素一直不出现，尝试显示下一个提示
				this.tryShowNextTip()
			}
		}
	}

	private checkForNewElements() {
		const newConfigs = this.configs.filter(
			config =>
				this.currentRoute.includes(config.route) &&
				!this.watchers.has(config.id) &&
				!this.shownTips.has(config.id)
		)

		newConfigs.forEach(config => {
			this.startWatching(config)
		})
	}

	private startVisibilityCheck(configId: string, options: Partial<VisibilityOptions> = {}) {
		const watcher = this.watchers.get(configId)
		if (!watcher || watcher.hasShown) return

		const { element } = watcher
		if (!element) {
			this.checkElementExistence(configId)
			return
		}

		const { minWidth = 1, minHeight = 1, minOpacity = 0.1, stableTime = 300 } = options
		const rect = element.getBoundingClientRect()
		const style = window.getComputedStyle(element)
		const opacity = parseFloat(style.opacity)

		const isFullyVisible =
			rect.width >= minWidth &&
			rect.height >= minHeight &&
			opacity >= minOpacity &&
			style.display !== 'none' &&
			style.visibility !== 'hidden' &&
			rect.top < window.innerHeight &&
			rect.bottom > 0 &&
			rect.left < window.innerWidth &&
			rect.right > 0

		const now = Date.now()

		if (isFullyVisible) {
			if (!watcher.isVisible) {
				watcher.isVisible = true
				watcher.visibleStartTime = now
			}

			const stableDuration = now - watcher.visibleStartTime
			if (stableDuration >= stableTime) {
				this.showTip(configId)
				return
			}
		} else {
			watcher.isVisible = false
		}

		watcher.checkTimer = window.setTimeout(() => {
			this.startVisibilityCheck(configId, options)
		}, 100)
	}

	private async showTip(configId: string) {
		const watcher = this.watchers.get(configId)
		if (!watcher || watcher.hasShown || !watcher.element) return

		if (this.isShowingTip) {
			watcher.checkTimer = window.setTimeout(() => {
				this.startVisibilityCheck(configId)
			}, 500)
			return
		}

		watcher.hasShown = true
		if (watcher.checkTimer) clearTimeout(watcher.checkTimer)
		if (watcher.elementCheckTimer) clearTimeout(watcher.elementCheckTimer)

		this.shownTips.add(configId)
		// console.log(configId,'================tiptip');

		this.saveShownTips()
	
		this.isShowingTip = true

		const position = this.calculatePosition(watcher.config, watcher.element)
		this.currentTip.value = watcher.config
		this.tipPosition.value = position
		this.isVisible.value = true
// 调用接口记录提示显示
  await this.recordTipShown(configId)
		this.watchers.delete(configId)
	}
// 新增方法：记录提示已显示并调用接口
private async recordTipShown(configId: string) {
  try {
    const params = {
      featureRemindId: configId,
      isChecked: 1
    }
    const res = await addBubbleRecord(params)
    if (res?.success) {
      console.log(`成功记录提示显示: ${configId}`)
    } else {
      console.warn(`记录提示显示失败: ${configId}`)
    }
  } catch (error) {
    console.error(`调用接口失败: ${configId}`, error)
  }
}
	public calculatePosition(config: UpdateTipConfig, element: HTMLElement) {
		const rect = element.getBoundingClientRect()
		const { position, offsetX = 0, offsetY = 10 } = config

		const style: Record<string, any> = {
			position: 'fixed',
			zIndex: 9999
		}
		switch (position) {
			case 'top':
				style['left'] = `${rect.left + rect.width / 2 + offsetX}px`
				style['top'] = `${rect.top - offsetY}px`
				style['transform'] = 'translate(-50%, -100%)'
				break
			case 'bottom':
				style['left'] = `${rect.left + rect.width / 2 + offsetX}px`
				style['top'] = `${rect.bottom + offsetY}px`
				style['transform'] = 'translate(-50%, 0)'
				break
			case 'left':
				style['left'] = `${rect.left - offsetX}px`
				style['top'] = `${rect.top + rect.height / 2 + offsetY}px`
				style['transform'] = 'translate(-100%, -50%)'
				break
			case 'right':
				style['left'] = `${rect.right + offsetX}px`
				style['top'] = `${rect.top + rect.height / 2 + offsetY}px`
				style['transform'] = 'translate(0, -50%)'
				break
		}
		return style
	}

	// 修改关闭方法，尝试显示下一个提示
	closeTip() {
		if (this.currentTip.value) {
			this.shownTips.add(this.currentTip.value.id)
			this.saveShownTips()
		}

		this.isVisible.value = false
		this.currentTip.value = null
		this.tipPosition.value = {}
		this.isShowingTip = false

		// 显示下一个提示
		this.tryShowNextTip()
	}

	// 尝试显示下一个提示
	private tryShowNextTip() {
		if (this.pendingTips.length > 0) {
			// 从队列中取出下一个提示
			const nextTip = this.pendingTips.shift()!
			this.startWatching(nextTip)
		} else {
			// 没有更多提示了，检查是否有其他需要显示的提示
			setTimeout(() => {
				this.checkPendingWatchers()
			}, 500)
		}
	}

	private checkPendingWatchers() {
		for (const [configId, watcher] of this.watchers) {
			if (!watcher.hasShown && watcher.element) {
				this.startVisibilityCheck(configId)
			}
		}
	}

	private clearWatchers() {
		this.watchers.forEach(watcher => {
			if (watcher.checkTimer) clearTimeout(watcher.checkTimer)
			if (watcher.elementCheckTimer) clearTimeout(watcher.elementCheckTimer)
		})
		this.watchers.clear()
	}

	showTipById(configId: string) {
		const config = this.configs.find(c => c.id === configId)
		if (!config) return

		const element = document.querySelector(config.selector) as HTMLElement
		if (element) {
			const position = this.calculatePosition(config, element)
			this.currentTip.value = config
			this.tipPosition.value = position
			this.isVisible.value = true
		}
	}

	private  saveShownTips() {
		try {
			localStorage.setItem('update-tips-shown', JSON.stringify(Array.from(this.shownTips)))
			// console.log('已保存已显示的提示', this.currentTip.value?.id)

		} catch (error) {
			console.warn('Failed to save shown tips:', error)
		}
	}

	public clearShownTips() {
		this.shownTips.clear()
		localStorage.removeItem('update-tips-shown')
	}

	destroy() {
		this.clearWatchers()
		this.mutationObserver?.disconnect()
	}
}

export const updateTipsManager = new UpdateTipsManager()

if (typeof window !== 'undefined') {
	;(window as any).updateTipsManager = updateTipsManager
}
import { getFeatureRemindList } from '@/api/guide'

// 修改模拟数据，添加priority字段
export async function fetchUpdateTips(): Promise<UpdateTipConfig[]> {
	let res = await getFeatureRemindList()
	if (res?.success) {
		return res.data
	}
	// return [
	//   {
	//     id: 'user-wrap-tip-2024',
	//     route: '/videoEdit',
	//     selector: '#animation-wrap',
	//     position: 'top',
	//     offsetY: 15,
	//     name: '视频草稿新增生成MV功能',
	//     content: '普通的视频看起来无聊？对当前视频草稿AI一键生成适配的歌词和音乐，直接输出MV版视频，日常视频唱着也能讲出来~',
	//     url:egGif,
	//     priority: 3 // 最高优先级
	//   },
	//   {
	//     id: 'home-second-tip-2024',
	//     route: '/videoEdit',
	//     selector: '#mv-line-wrap',
	//     position: 'bottom',
	//     offsetY: 10,
	//     name: '视频编辑器支持图片上轨',
	//     content: '视频编辑器支持图片素材上轨，可为图片素材添加放大、缩小、上下左右方向移动动画。在素材库中目前也支持直接上传图片素材成片。',
	//     url:egGif,
	//     priority: 2
	//   },
	//   {
	//     id: 'timeline-optimization',
	//     route: '/videoEdit',
	//     selector: '#aihuman-wrap',
	//     position: 'top',
	//     offsetY: 10,
	//     name: '编辑器新增数字人组件',
	//     content: '在草稿中直接插入数字人形象，让TA帮你完成宣传科普、跟唱歌曲等场景。',
	//     url:egGif,
	//     priority: 1
	//   },

	//   {
	//     id: 'mv-btn-tip',
	//     route: '/aiMusic',
	//     selector: '#mv-btn',
	//     position: 'top',
	//     offsetX: 0,
	//     name: 'AI音乐新增生成MV功能',
	//     content: '选择一首音乐，AI自动匹配节奏生成适配画面，从旋律到视觉全程呼应，让耳朵听到的感动，眼睛也能看见，分享到平台自带吸睛属性~',
	//     url:egGif,
	//     priority: 1
	//   }
	// ]
}

export async function initUpdateTips(router: Router) {
	updateTipsManager.setRouter(router)
	try {
		const configs = await fetchUpdateTips()
		updateTipsManager.setConfigs(configs)
	} catch (error) {
		console.warn('Failed to fetch update tips:', error)
	}
}
